/**
 * MCP connection details are edited on the Add/Edit Agent main form but remain
 * stored inside policy.json's `qz_pol`, which is the authoritative document and
 * the source the AGENT_ATOM is built from. These helpers move the values
 * between the two without changing the file's shape.
 */

export const DEFAULT_AUTH_FORMAT = "raw";

export const AUTH_FORMAT_OPTIONS = [
  { value: "raw", label: "Raw" },
  { value: "jwt", label: "JWT" },
  { value: "bearer", label: "Bearer" },
];

/** Marker stored in `mcp_auth_token` that makes Gluon Link mint a runtime OIDC token. */
export const RUNTIME_OIDC_TOKEN = "__RUNTIME_OIDC__";

/** `bearer` is only honoured for native providers, so it is not offered here. */
export const PROVIDER_AUTH_FORMAT_OPTIONS = [
  { value: "raw", label: "Raw" },
  { value: "jwt", label: "JWT" },
];

export const EMPTY_CONNECTION = {
  mcp_connection: "",
  mcp_auth_token: "",
  auth_format: DEFAULT_AUTH_FORMAT,
  auth_header: "",
};

export const CONNECTION_FIELDS = Object.keys(EMPTY_CONNECTION);

/** Narrow an object of form values down to just the connection fields. */
export const pickConnection = (source = {}) => ({
  mcp_connection: source.mcp_connection || "",
  mcp_auth_token: source.mcp_auth_token || "",
  auth_format: source.auth_format || DEFAULT_AUTH_FORMAT,
  auth_header: source.auth_header || "",
});

/**
 * Read connection values out of a policy.json string.
 * Returns null when the content is absent or unparseable so callers can leave
 * the form untouched rather than blanking fields from a bad parse.
 */
export const readConnectionFromPolicy = (policyJson) => {
  if (!policyJson) return null;
  try {
    const parsed = JSON.parse(policyJson);
    if (!parsed || typeof parsed !== "object" || !parsed.qz_pol) return null;
    return pickConnection(parsed.qz_pol);
  } catch {
    return null;
  }
};

/** Read `qz_pol.provider`, or null when unavailable. */
export const readProviderFromPolicy = (policyJson) => {
  if (!policyJson) return null;
  try {
    return JSON.parse(policyJson)?.qz_pol?.provider || null;
  } catch {
    return null;
  }
};

/**
 * Write connection values into a policy.json string, preserving every other
 * key. Unparseable content is returned untouched so a keystroke on the main
 * form can never destroy an in-progress hand edit in the JSON view.
 */
export const writeConnectionToPolicy = (policyJson, connection, provider) => {
  if (!policyJson) return policyJson;

  let parsed;
  try {
    parsed = JSON.parse(policyJson);
  } catch {
    return policyJson;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return policyJson;

  const next = pickConnection(connection);
  const qz_pol = {
    ...(parsed.qz_pol || {}),
    mcp_connection: next.mcp_connection,
    mcp_auth_token: next.mcp_auth_token,
    auth_format: next.auth_format,
  };

  // PolicyFormView omits an empty auth_header rather than writing "".
  if (next.auth_header) {
    qz_pol.auth_header = next.auth_header;
  } else {
    delete qz_pol.auth_header;
  }

  if (provider !== undefined && provider !== null) {
    qz_pol.provider = provider;
  }

  return JSON.stringify({ ...parsed, qz_pol }, null, 2);
};

/** True when any connection field differs between two sets of form values. */
export const connectionChanged = (a = {}, b = {}) => {
  const left = pickConnection(a);
  const right = pickConnection(b);
  return CONNECTION_FIELDS.some((field) => left[field] !== right[field]);
};
